import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import './AdminPanel.css';

function AdminPanel() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');

  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    tags: '',
    category: 'vectors',
    file: null
  });

  const safeAssets = Array.isArray(assets) ? assets : [];

  useEffect(() => {
    checkAuth();
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAssets();
    }
  }, [isAuthenticated]);

  const checkAuth = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setAuthChecking(false);
      return;
    }
    try {
      const res = await fetch('/api/admin/verify', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setIsAuthenticated(data.valid === true);
    } catch {
      setIsAuthenticated(false);
    }
    setAuthChecking(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      const data = await res.json();
      if (res.ok && data.token) {
        localStorage.setItem('adminToken', data.token);
        setIsAuthenticated(true);
      } else {
        setLoginError(data.error || 'Login failed');
      }
    } catch {
      setLoginError('Connection error. Please try again.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setAssets([]);
  };

  const fetchAssets = async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const response = await fetch('/api/assets');
      if (!response.ok) throw new Error('Failed to fetch assets');
      const data = await response.json();
      const list =
        Array.isArray(data) ? data :
        Array.isArray(data?.assets) ? data.assets :
        Array.isArray(data?.data) ? data.data :
        [];
      setAssets(list);
    } catch (error) {
      console.error('Error fetching assets:', error);
      setAssets([]);
      setErrorMsg('Could not load assets. Make sure your backend (/api) is working.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      file: e.target.files?.[0] || null
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file || !formData.title || !formData.category) {
      alert('Please fill in all required fields');
      return;
    }
    setUploading(true);
    setErrorMsg('');
    const uploadFormData = new FormData();
    uploadFormData.append('file', formData.file);
    uploadFormData.append('title', formData.title);
    uploadFormData.append('tags', formData.tags);
    uploadFormData.append('category', formData.category);
    try {
      // Use fetch for file upload
      const response = await fetch('/api/assets', {
        method: 'POST',
        body: uploadFormData,
        // Do NOT set Content-Type header; browser will set it with boundary
        credentials: 'same-origin',
      });
      if (!response.ok) {
        let msg = 'Unknown error';
        try {
          const data = await response.json();
          msg = data?.error || response.statusText;
        } catch {}
        if (response.status === 405) {
          msg = 'Server error: Method not allowed. The backend server may not be running. Check PM2 status on the server.';
        } else if (response.status === 404) {
          msg = 'Server error: API endpoint not found. Check if the server is running and configured correctly.';
        }
        setErrorMsg(`Upload failed: ${msg}`);
        alert(`Upload failed: ${msg}`);
        return;
      }
      alert('Asset uploaded successfully!');
      setFormData({ title: '', tags: '', category: 'vectors', file: null });
      const fileInput = document.getElementById('file-input');
      if (fileInput) fileInput.value = '';
      fetchAssets();
    } catch (error) {
      setErrorMsg('Upload failed: ' + (error?.message || 'Unknown error'));
      alert('Upload failed: ' + (error?.message || 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;
    setErrorMsg('');
    try {
      const response = await fetch(`/api/assets/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete asset');
      alert('Asset deleted successfully!');
      fetchAssets();
    } catch (error) {
      console.error('Error deleting asset:', error);
      setErrorMsg('Failed to delete asset. Make sure backend delete API is working.');
      alert('Failed to delete asset. Please try again.');
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('This will remove all assets whose files are missing. Continue?')) return;
    setErrorMsg('');
    try {
      const response = await fetch('/api/assets/cleanup', { method: 'POST' });
      if (!response.ok) throw new Error('Failed to cleanup assets');
      const data = await response.json();
      alert(`Cleanup completed! Removed ${data.removed} orphaned asset(s).`);
      fetchAssets();
    } catch (error) {
      console.error('Error cleaning up assets:', error);
      setErrorMsg('Failed to cleanup assets. Make sure backend cleanup API is working.');
      alert('Failed to cleanup assets. Please try again.');
    }
  };

  if (authChecking) {
    return (
      <div className="admin-panel">
        <Header />
        <div className="admin-container">
          <div className="loading">Checking authentication...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="admin-panel">
        <Header />
        <div className="admin-container">
          <div className="login-section">
            <h1 className="admin-title">Admin Login</h1>
            <form className="login-form" onSubmit={handleLogin}>
              {loginError && (
                <div className="login-error">{loginError}</div>
              )}
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  value={loginData.username}
                  onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                  required
                  autoComplete="username"
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  autoComplete="current-password"
                />
              </div>
              <button type="submit" className="submit-button">Login</button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <Header />

      <div className="admin-container">
        <div className="admin-header-row">
          <h1 className="admin-title">Admin Panel</h1>
          <button onClick={handleLogout} className="logout-button">Logout</button>
        </div>

        {errorMsg && (
          <div style={{ marginBottom: 16, padding: 12, border: '1px solid #f99', background: '#fff5f5' }}>
            {errorMsg}
          </div>
        )}

        <div className="admin-content">
          <div className="upload-section">
            <h2 className="section-title">Upload New Asset</h2>

            <form className="upload-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter asset title"
                />
              </div>

              <div className="form-group">
                <label htmlFor="category">Category *</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="vectors">Vectors</option>
                  <option value="photos">Photos</option>
                  <option value="psds">PSDs</option>
                  <option value="icons">Icons</option>
                  <option value="templates">Templates</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="tags">Tags (comma-separated)</label>
                <input
                  type="text"
                  id="tags"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  placeholder="e.g., design, modern, ui"
                />
              </div>

              <div className="form-group">
                <label htmlFor="file-input">File *</label>
                <input
                  type="file"
                  id="file-input"
                  name="file"
                  onChange={handleFileChange}
                  required
                  accept=".jpg,.jpeg,.png,.gif,.svg,.psd,.fig,.pdf,.zip"
                />
              </div>

              <button type="submit" className="submit-button" disabled={uploading}>
                {uploading ? 'Uploading...' : 'Upload Asset'}
              </button>
            </form>
          </div>

          <div className="assets-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 className="section-title">All Assets ({safeAssets.length})</h2>
              <button
                onClick={handleCleanup}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f0f0f0',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cleanup Orphaned Assets
              </button>
            </div>

            {loading ? (
              <div className="loading">Loading assets...</div>
            ) : safeAssets.length === 0 ? (
              <div className="empty-state">No assets uploaded yet.</div>
            ) : (
              <div className="assets-list">
                {safeAssets.map((asset, idx) => {
                  // ✅ tags safe: array only
                  const tagsArray = Array.isArray(asset?.tags) ? asset.tags : [];

                  // ✅ date safe
                  const dateText = asset?.uploadDate
                    ? new Date(asset.uploadDate).toLocaleDateString()
                    : '-';

                  return (
                    <div key={asset?.id ?? idx} className="asset-item">
                      <div className="asset-item-info">
                        <h3 className="asset-item-title">{asset?.title ?? 'Untitled'}</h3>

                        <div className="asset-item-meta">
                          <span className="asset-item-category">{asset?.category ?? '-'}</span>
                          <span className="asset-item-date">{dateText}</span>

                          {Number(asset?.downloadCount) > 0 && (
                            <span className="asset-item-downloads">
                              {asset.downloadCount} downloads
                            </span>
                          )}
                        </div>

                        {tagsArray.length > 0 && (
                          <div className="asset-item-tags">
                            {tagsArray.map((tag, index) => (
                              <span key={index} className="tag-badge">{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>

                      <button
                        className="delete-button"
                        onClick={() => handleDelete(asset?.id)}
                        disabled={!asset?.id}
                      >
                        Delete
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
